import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { REPORTS_BATCH_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/reports-batch.ts';
import {
  CreateReportTemplateRequest,
  GetReportTemplateResponse,
  GetReportTemplatesListResponse,
  GetReportTemplatesListV1ReportsTemplatesGetParams,
  UpdateReportTemplateRequest,
} from 'compliance-api';

export const REPORT_TEMPLATE_QUERY_KEY = 'compliance/report-template';
export const REPORTS_TEMPLATES_LIST_QUERY_KEY = 'compliance/reports-templates-list';

export const useGetReportsTemplatesQuery = (
  payload: GetReportTemplatesListV1ReportsTemplatesGetParams,
  disabled = false,
) => {
  return useInfiniteQuery<GetReportTemplatesListResponse>({
    queryKey: [
      REPORTS_TEMPLATES_LIST_QUERY_KEY,
      JSON.stringify(payload),
      useGetCurrentBusinessId(),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getReportTemplatesListV1ReportsTemplatesGet({
        ...payload,
        page:
          typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || payload.page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useGetTemplateQuery = (id: number, disabled = false) => {
  return useQuery<GetReportTemplateResponse>({
    queryKey: [REPORT_TEMPLATE_QUERY_KEY, id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getReportTemplateV1ReportsTemplateGet({ id });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useCreateReportTemplateMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateReportTemplateRequest) => {
      const response = await withErrorHandling(
        api.v1.createReportTemplateV1ReportsTemplatesPost(payload),
        {
          loading: 'Creating template...',
          success: 'Template created',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_TEMPLATES_LIST_QUERY_KEY]);
    },
  });
};

export const useUpdateReportTemplateMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateReportTemplateRequest) => {
      const response = await withErrorHandling(
        api.v1.updateReportTemplateV1ReportsTemplatePatch(payload),
        {
          loading: 'Updating template...',
          success: 'Template updated',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_BATCH_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([REPORT_TEMPLATE_QUERY_KEY]);
      queryClient.invalidateQueries([REPORTS_TEMPLATES_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteReportTemplateMutation = () => {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteReportTemplateV1ReportsTemplatesIdDelete({ id: id }),
        {
          loading: 'Deleting template...',
          success: 'Template deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([REPORTS_TEMPLATES_LIST_QUERY_KEY]);
    },
  });
};
